/**
 * GPC (ABO) statements as a CSV file, for the free converter on the public
 * site (`/prevod-gpc-do-csv`). Pure: runs in the visitor's browser on the
 * output of `parseGpc`, nothing is uploaded.
 *
 * Two dialects:
 * - `cs`: semicolon-separated with a decimal comma, which Czech Excel opens
 *   into columns without an import wizard;
 * - `en`: comma-separated with a decimal point.
 * Both start with a UTF-8 byte order mark so Excel reads Czech diacritics.
 * Text cells go through `csvCell`, so a counterparty name can never run as a
 * spreadsheet formula.
 *
 * The same columns also go to an Excel workbook (`gpcSheetRows`): the page
 * loads SheetJS only when the visitor asks for XLSX.
 */
import { csvCell, neutralizeFormula } from "@/lib/csv/csvCell";
import type { GpcStatement } from "./gpcParser";

export type CsvDialect = "cs" | "en";

const HEADERS: Record<CsvDialect, string[]> = {
  cs: [
    "Účet",
    "Datum",
    "Částka",
    "Protiúčet",
    "Kód banky",
    "Protistrana",
    "Variabilní symbol",
    "Konstantní symbol",
    "Specifický symbol",
    "Číslo dokladu",
  ],
  en: [
    "Account",
    "Date",
    "Amount",
    "Counter account",
    "Bank code",
    "Counterparty",
    "Variable symbol",
    "Constant symbol",
    "Specific symbol",
    "Document number",
  ],
};

const BOM = String.fromCharCode(0xfeff);

function formatAmount(amount: number, dialect: CsvDialect): string {
  const fixed = amount.toFixed(2);
  return dialect === "cs" ? fixed.replace(".", ",") : fixed;
}

function accountOf(statement: GpcStatement): string {
  return statement.accountPrefix
    ? `${statement.accountPrefix}-${statement.account}`
    : statement.account;
}

/** Every movement of every statement in the file, one row each, in file order. */
export function gpcToCsv(statements: readonly GpcStatement[], dialect: CsvDialect): string {
  const sep = dialect === "cs" ? ";" : ",";
  const rows = [HEADERS[dialect].map((h) => csvCell(h)).join(sep)];
  for (const statement of statements) {
    const account = accountOf(statement);
    for (const item of statement.items) {
      rows.push(
        [
          csvCell(account),
          csvCell(item.postingDate),
          csvCell(formatAmount(item.amount, dialect), { text: false }),
          csvCell(item.counterAccount),
          csvCell(item.counterBankCode),
          csvCell(item.counterName),
          csvCell(item.variableSymbol),
          csvCell(item.constantSymbol),
          csvCell(item.specificSymbol),
          csvCell(item.documentId),
        ].join(sep)
      );
    }
  }
  return `${BOM}${rows.join("\r\n")}\r\n`;
}

/**
 * Rows for an Excel sheet: the CSV's header and columns, with the amount as a
 * number (so Excel can sum it) and every other cell as text (dates stay
 * `YYYY-MM-DD`, symbols keep their leading zeros). SheetJS writes strings as
 * values, never as formulas; text that would read as a formula still gets
 * the CSV's apostrophe, so a sheet re-saved as CSV stays safe.
 */
export function gpcSheetRows(
  statements: readonly GpcStatement[],
  dialect: CsvDialect
): (string | number)[][] {
  const rows: (string | number)[][] = [HEADERS[dialect]];
  for (const statement of statements) {
    const account = accountOf(statement);
    for (const item of statement.items) {
      const text = (value: string | null | undefined) => neutralizeFormula(value ?? "");
      rows.push([
        text(account),
        text(item.postingDate),
        Math.round(item.amount * 100) / 100,
        text(item.counterAccount),
        text(item.counterBankCode),
        text(item.counterName),
        text(item.variableSymbol),
        text(item.constantSymbol),
        text(item.specificSymbol),
        text(item.documentId),
      ]);
    }
  }
  return rows;
}

/** `vypis-2026-10-31.csv`: named after the newest statement's file date. */
export function csvFileName(
  statements: readonly GpcStatement[],
  dialect: CsvDialect,
  extension: "csv" | "xlsx" = "csv"
): string {
  const dates = statements
    .map((s) => s.fileDate)
    .filter(Boolean)
    .sort();
  const stem = dialect === "cs" ? "vypis" : "statement";
  const date = dates[dates.length - 1];
  return date ? `${stem}-${date}.${extension}` : `${stem}.${extension}`;
}
